import { AlertType, HockeyLesson, Season } from '@types';
import {
  AlertModal,
  Card,
  EmptyState,
  LoadingSpinner,
  SectionHeader,
} from '_atoms';
import { SeasonDetailsService } from '_services';
import HockeyLessonService from '_services/HockeyLessonService';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdAttachMoney,
  MdInfo,
  MdPeople,
  MdSportsHockey,
} from 'react-icons/md';

interface SelectHockeyLessonProps {
  selectedLesson?: HockeyLesson;
  onNext: (data?: any) => void;
  onStepComplete: (data: any) => void;
}

export const SelectHockeyLesson: React.FC<SelectHockeyLessonProps> = ({
  selectedLesson,
  onNext,
  onStepComplete,
}) => {
  const [lessons, setLessons] = useState<HockeyLesson[]>([]);
  const [selectedLessonState, setSelectedLessonState] = useState<
    HockeyLesson | undefined
  >(selectedLesson);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const loadHockeyLessons = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseData = await HockeyLessonService.getHockeyLessons();
      if (responseData && Array.isArray(responseData)) {
        setLessons(responseData.filter(lesson => lesson.isActive));
      } else {
        setLessons([]);
      }
    } catch (error) {
      console.error('Error loading hockey lessons:', error);
      showAlert('error', 'Error', 'Failed to load hockey lessons.');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadCurrentSeason();
    loadHockeyLessons();
  }, [loadHockeyLessons]);

  const loadCurrentSeason = async () => {
    setIsLoadingInitial(true);
    try {
      const responseData =
        (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

      if (responseData) {
        setSettings(responseData);
      }
    } catch (error) {
      console.error('Error loading current season:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleLessonSelect = (lesson: HockeyLesson) => {
    setSelectedLessonState(lesson);
  };

  const handleContinue = () => {
    if (selectedLessonState) {
      onStepComplete({ selectedLesson: selectedLessonState });
      onNext({ selectedLesson: selectedLessonState });
    } else {
      showAlert(
        'error',
        'Selection Required',
        'Please select a hockey lesson to continue.',
      );
    }
  };

  const NotInSeason = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md justify-center items-center border-none">
        <div className="text-center space-y-4">
          <MdInfo className="mx-auto text-gray-400 text-6xl" />
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Hockey Lessons Not Available
            </h3>
            <p className="text-gray-600">
              Hockey lesson purchases are currently not enabled. Please check
              back later or contact us for more information.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (!settings.isHockeyLessonPurchasesEnabled && !isLoadingInitial) {
    return <NotInSeason />;
  }

  if (lessons.length === 0) {
    return (
      <EmptyState
        title="No Hockey Lessons Available"
        description="There are currently no active hockey lessons available for registration."
        icon={MdSportsHockey}
      />
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        icon={MdSportsHockey}
        title="Select a Hockey Lesson"
        iconColor="text-blue-600"
      />

      <div className="grid gap-4 md:grid-cols-2">
        {lessons.map(lesson => (
          <div
            key={lesson.id}
            className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
              selectedLessonState?.id === lesson.id
                ? 'ring-2 ring-blue-500 bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
            onClick={() => handleLessonSelect(lesson)}
          >
            <Card>
              <div className="p-4 h-full flex flex-col">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {lesson.title}
                  </h3>
                  <p
                    className="text-gray-600 text-sm mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-2"
                    dangerouslySetInnerHTML={{
                      __html: lesson.description,
                    }}
                  />

                  {lesson.slotsAvailable <= 0 && (
                    <div className="mt-3 p-2 bg-red-100 text-red-700 text-sm rounded">
                      Sold Out
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full mr-3">
                      <MdAttachMoney className="text-green-600 text-xl" />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Price
                      </span>
                      <div className="text-lg font-bold text-green-700">
                        ${lesson.price}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full mr-3">
                      <MdPeople className="text-blue-600 text-xl" />
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">
                        Availability
                      </span>
                      <div className="text-sm font-semibold text-gray-900">
                        {lesson.slotsAvailable <= 0 ? 0 : lesson.slotsAvailable}{' '}
                        of {lesson.maxCapacity}
                      </div>
                      <div
                        className={`text-xs ${
                          lesson.slotsAvailable <= 0
                            ? 'text-red-600'
                            : lesson.slotsAvailable <= 3
                              ? 'text-orange-600'
                              : 'text-green-600'
                        }`}
                      >
                        {lesson.slotsAvailable <= 0
                          ? 'Sold Out'
                          : lesson.slotsAvailable <= 3
                            ? 'Limited Spots'
                            : 'Available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-6">
        <button
          onClick={handleContinue}
          disabled={
            !selectedLessonState || selectedLessonState.slotsAvailable < 1
          }
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  );
};
